import * as THREE from "three";
import { LevelSession, OfflineSeedProvider } from "@highmarch/app";
import { BalanceData, Cell, CellKind, LevelSim, SimConstants, SimEventKind, TileKind } from "@highmarch/domain";
import { audio } from "../audio/audio";
import { HitStop } from "../level/hitStop";
import { LevelScene } from "../level/levelScene";
import { SimClock } from "../level/simClock";
import { motionOk } from "../ui/kit";
import { reducedVfx } from "../ui/settings";
import type { Biome } from "../ui/chapters";
import { levelAssetNeeds } from "./assetNeeds";
import { GraphicsPrefs } from "./graphicsPrefs";
import { GroundMesh } from "./groundMesh";
import { HqMaterials } from "./hqMaterials";
import { LOOKS, Look } from "./look";
import { tierIndex } from "./quality";
import type { SceneRig } from "./scene";
import { SkyClouds } from "./skyClouds";
import { WornSkins } from "./skins/wornSkins";
import { hqEnemyName } from "./views";
import { DetailScatter } from "./views/detail";
import { EnemyWash } from "./views/enemyFeel";
import { FoliageView } from "./views/foliage";
import { TOWER_SHOT } from "./views/projectiles";

/**
 * The showcase the title stages (owner, 2026-10-06: the boot splash, the launch image and the title must be one
 * picture, "a still from the animation that plays in the background"). It is the same for every player, so its
 * opening frame can be rendered once (tools/video/title_still.mjs) and shipped as the still: the iOS launch image,
 * the boot splash and the title's first frame (art/interface/title/showcase.jpg), and the live scene starts on
 * exactly that frame. It is the old key art's staged battle (capture.mjs `keyscene`): level 13's ground, the road
 * winding over the gorge on its plank bridge, seven kinds of tower, a mixed wave, golden hour.
 */
export const SHOWCASE = {
  level: 13,
  biome: "meadow" as Biome,
  /** Fortress gate outward (capture.mjs KS_ROAD); gorge cells become bridge tiles. */
  road: "7,2 7,3 7,4 7,5 7,6 7,7 6,7 5,7 4,7 4,8 4,9 4,10 4,11 5,11 5,12 5,13 4,13 3,13",
  /** archetype index, tier, cell (towers.json order; capture.mjs KS_TOWERS) */
  towers: [[0, 3, 6, 4], [4, 3, 8, 7], [1, 3, 8, 8], [2, 2, 3, 7], [3, 3, 3, 9], [5, 3, 3, 11], [6, 2, 6, 12]] as const,
  /** enemies.json indices the director sends (capture.mjs KS_WAVE's kinds) */
  wave: [0, 1, 2, 3, 4, 5, 6] as const,
  /** The opening shot, as the key art framed it at 393×852: eye, look point, vertical field of view. */
  eye: { x: 11.5, y: 4.5, z: -3 },
  at: { x: 6.5, y: 1.2, z: 6 },
  fov: 36,
  /** The still's aspect (width / height): other screens show it cover-cropped, and the live lens crops the same way. */
  aspect: 393 / 852,
  /** The key art's seed (capture.mjs keyscene: the "keyart" player's): the ground's relief the shot was framed on. */
  seed: new OfflineSeedProvider().levelSeed("keyart", 13, 0),
  /**
   * The opening wave, as the key art staged it: this many raiders a few ticks apart, tough enough to walk in. 0 since
   * M52 (Kevin: the launch picture "with the units looking like this doesn't look very good before the animation
   * kicks in"): frozen in the still, the raiders stood off the cobbles in their spawn shimmer and shield bubbles. The
   * still, the launch images and the live title's first frame are now the quiet road, and the director's raiders
   * march in from the portal a moment after the title goes live.
   */
  openingWave: 0,
  openingGapTicks: 4,
  openingHp: 6,
  /** …until the lead is this far from the fortress along the road (0 = the gate, 1 = the portal). */
  openingFront: 0.24,
};

/**
 * The title screen's live diorama: the showcase above, running live under a slow cinematic camera. It is the game's
 * own level world (level/levelScene.ts) over a sandboxed sim (no draft, no defeat, no stage spawns), with a small
 * director feeding it raiders so the fight never ends and never overruns the walls.
 *
 * Boot loads its models first (`assetNames`) and builds it behind the loading card, so by TAP TO START it is already
 * playing; the title shows the still until it has drawn its first frames, then cross-fades (ui/title.ts). The camera
 * starts on the still's exact pose and lens (cover-cropped like the picture on any screen shape), the battle starts on
 * the same pre-rolled state, and the player's skins are set aside while it stands, so the two match.
 *
 * Presentation only: its sim is thrown away with it and never touches the save. Battle sounds are held so the title
 * music plays alone (audio.holdSfx). `dispose` hands the rig back as it found it: iso camera, look, haze, shadows.
 */
export class TitleDiorama {
  /** From this quality preset down (and with Reduced effects or reduced motion) the title keeps its painted still. */
  static readonly MinTier = "low";
  /** Raiders on the road at once, and the seconds between arrivals. */
  static readonly MaxAlive = 8;
  static readonly SpawnEvery = [0.8, 1.5] as const;
  /** Longest the opening wave may walk before the first frame (s). */
  static readonly PreRollMax = 40;
  /** One slow sweep of the camera, side to side and back (s). */
  static readonly DriftSeconds = 48;
  /** Seconds over which the drift (and the device lean) eases in from the opening shot. */
  static readonly DriftEaseIn = 5;

  private readonly session: LevelSession;
  private readonly scene: LevelScene;
  private readonly sky: SkyClouds;
  private readonly hitStop = new HitStop();
  private readonly clock = new SimClock();
  private readonly look: Look;
  private readonly prevFog: [number, number];
  private readonly prevWorn: unknown;
  private readonly enemyPool: number[];
  private hpMult = 2;
  /** Raiders of the opening wave (ids up to this): tough on purpose, so the director does not learn from them. */
  private openingIds = 0;
  private spawnIn = 0;
  private time = 0;
  private raf = 0;
  private last = 0;
  private disposed = false;
  private seed = 1;
  /** Device tilt / pointer, −1..1 each way (the title passes its own): leans the camera a little. */
  lean = { x: 0, y: 0 };
  /** Frames drawn since `start` (the title fades in once a few have landed: the first ones compile shaders). */
  frames = 0;
  /** Held on the opening frame (the still's capture, tools/video/title_still.mjs): no time passes. */
  frozen = false;

  /** Whether this device and these settings get the live title at all (else the still, with CSS motion). */
  static wanted(search = typeof location === "object" ? location.search : ""): boolean {
    const p = new URLSearchParams(search);
    if (p.has("diorama")) return true;                       // captures force it on, whatever the tier
    if (p.has("lite") || p.has("nodiorama") || p.has("sheet") || p.has("skiptitle")) return false;
    if (!motionOk() || reducedVfx()) return false;
    return GraphicsPrefs.presetIndex() < tierIndex(TitleDiorama.MinTier);
  }

  /** The HQ models the showcase stands: boot loads these first (filtered to what ships). */
  static assetNames(balance: BalanceData, shipped: ReadonlySet<string>): Set<string> {
    const L = balance.level(SHOWCASE.level);
    const enemies = SHOWCASE.wave.map((i) => balance.enemies[i].id);
    const out = levelAssetNeeds({
      towers: [], enemies, boss: enemies[0], biome: SHOWCASE.biome, props: (L.terrain?.props ?? []).map((p) => p.name),
      chasm: L.chasm.length > 0, skin: null, groundMesh: true, pet: null,      // the title's battle takes no pet
    }, shipped);
    for (const [a, tier] of SHOWCASE.towers) {
      const id = balance.towers[a].id;
      out.add(`tower_${id}_t${tier}`);
      const shot = TOWER_SHOT[id];
      if (shot) out.add(shot.model);
    }
    return new Set([...out].filter((n) => shipped.has(n)));
  }

  constructor(private readonly rig: SceneRig, balance: BalanceData) {
    // The low camera looks across the land toward the horizon: it must carry on past the map edge (the play view
    // dissolves it a few cells out). The ground and props read the band when they are built.
    this.prevFog = [GroundMesh.FogStart, GroundMesh.FogFull];
    GroundMesh.FogStart = 6; GroundMesh.FogFull = 15;
    EnemyWash.on = false;
    audio.holdSfx(true);
    // the showcase is the same for everyone: the player's skins are set aside while it stands (restored on dispose)
    const W = WornSkins as unknown as { worn: unknown };
    this.prevWorn = W.worn;
    W.worn = { towers: new Map(), fortress: null, troop: null, emblem: null, road: null };
    const level = SHOWCASE.level;
    const owned = balance.towers.map(() => true), levels = balance.towers.map(() => 1);
    this.session = new LevelSession(balance, level, SHOWCASE.seed, owned, levels);
    const sim = this.sim;
    sim.debugSandboxOn();
    // kills earn points, and points open card offers, which pause the whole battle until a card is picked: none here
    sim.nextOfferAt = Number.MAX_SAFE_INTEGER;
    layRoad(sim, SHOWCASE.road.split(" ").map((xy) => { const [x, y] = xy.split(",").map(Number); return new Cell(x, y); }));
    rig.frame(sim.grid);
    rig.setShadowUpdates("manual", SimConstants.TicksPerSecond / 2);
    rig.animatedCasters = 1;
    // the showcase's own biome for the props and scatter it builds now (the hub's comes back straight after)
    const biomes = [FoliageView.biome, DetailScatter.biome] as const;
    FoliageView.biome = DetailScatter.biome = SHOWCASE.biome;
    try { this.scene = new LevelScene(rig, sim, SHOWCASE.seed, level, this.hitStop, () => undefined); }
    finally { [FoliageView.biome, DetailScatter.biome] = biomes; }
    this.scene.occlude = false;
    this.scene.enemies.marks.group.visible = false;          // HP bars, badges and floating numbers are HUD, not art
    const valid = sim.collectValidTowerCells();
    for (const [a, tier, x, y] of SHOWCASE.towers) {
      let best: Cell | null = null, bd = Infinity;
      for (const c of valid) { const d = Math.hypot(c.x - x, c.y - y); if (d < bd) { bd = d; best = c; } }
      if (best) sim.debugAddTower(a, best, tier);
    }
    // The fortress's strike flash, seen this low, is a glowing screen-aligned card: it holds its fire (keyscene did too).
    for (const t of sim.towerSlots) if (t.alive && balance.towers[t.archetype].isBase) t.disabledTicksLeft = 1e9;
    this.scene.ground.setTowers(sim.towerSlots);
    this.scene.noteRoad(true);
    this.scene.flush();
    this.scene.towers.onTick();
    this.scene.towers.settle(12);
    this.enemyPool = [...SHOWCASE.wave];
    // golden hour, with the key art's bloom (the play look's blows the hits out at this distance)
    this.look = rig.lookNow;
    const g = LOOKS.goldenHour;
    rig.setLook({ ...g, exposure: 1.25, bloom: { ...g.bloom, strength: 0.3, threshold: 0.95 } });
    HqMaterials.hazeRange.value.set(GroundMesh.FogStart, GroundMesh.FogFull);
    // a busy road from the first frame, as the key art staged it: a dense wave walked in until its lead nears the gate
    this.openingWave();
    this.scene.enemies.interpolate(1, 0);
    this.sky = new SkyClouds(rig.scene, rig.camera);
    this.placeCamera(0);
  }

  get sim(): LevelSim { return this.session.sim; }

  /** Starts the frame loop (the caller has the canvas behind the still). */
  start(): void {
    if (this.raf || this.disposed) return;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  /** A jolt of the world (the logo landing): the camera's trauma shake, skipped under reduced motion. */
  thump(amount = 0.42): void { if (motionOk() && !this.frozen) this.rig.iso.addTrauma(amount); }

  private readonly frame = (now: number): void => {
    this.raf = requestAnimationFrame(this.frame);
    const dt = this.frozen ? 0 : Math.min(SimClock.MaxFrameSeconds, (now - this.last) / 1000);
    this.last = now;
    this.time += dt;
    const k = this.hitStop.step(dt);
    const ticks = this.clock.take(dt * k.sim);
    for (let i = 0; i < ticks; i++) this.tick();
    this.scene.update(dt, dt * k.sim, dt * k.fx, this.frozen ? 1 : this.clock.alpha, now);
    this.scene.flush();
    this.placeCamera(this.time);
    this.rig.update(dt);
    this.sky.update(dt);
    this.scene.router?.syncLights(this.rig.camera);
    this.rig.render();
    this.frames++;
  };

  /** The opening wave: spawned and walked in headless (the views' tick hooks run, no frames are drawn). */
  private openingWave(): void {
    if (SHOWCASE.openingWave <= 0) { this.openingIds = this.sim.nextEnemyId - 1; this.spawnIn = 2; return; }
    const sim = this.sim, road = sim.road.roadCells, n = road.length - 1;
    const front = () => { let f = 1; for (const e of sim.enemySlots) if (e.alive) f = Math.min(f, 1 - e.distance.toNumber() / n); return f; };
    let spawned = 0;
    for (let i = 0; i < TitleDiorama.PreRollMax * SimConstants.TicksPerSecond; i++) {
      if (spawned < SHOWCASE.openingWave && i % SHOWCASE.openingGapTicks === 0) {
        sim.debugSpawn(SHOWCASE.wave[spawned % SHOWCASE.wave.length], SHOWCASE.openingHp);
        spawned++;
      }
      this.tick(false);
      // the views' own clocks too (a raider's walk cycle, its arrival from the portal), as the frames would have run them
      this.scene.update(TICK, TICK, TICK, 1, i * TICK * 1000);
      if (spawned > 0 && front() <= SHOWCASE.openingFront) break;
    }
    this.openingIds = sim.nextEnemyId - 1;
    this.spawnIn = 2;                                       // the director's first raider follows a little after
  }

  /** One sim tick and the views' per-tick hooks, with the director's spawns ahead of it (`direct`). */
  private tick(direct = true): void {
    const sim = this.sim;
    this.spawnIn -= 1 / SimConstants.TicksPerSecond;
    if (direct && this.spawnIn <= 0 && sim.enemySlots.filter((e) => e.alive).length < TitleDiorama.MaxAlive) {
      sim.debugSpawn(this.enemyPool[Math.floor(this.random() * this.enemyPool.length)], this.hpMult);
      const [a, b] = TitleDiorama.SpawnEvery;
      this.spawnIn = a + this.random() * (b - a);
    }
    sim.offerOpen = false;
    this.session.tick();
    // The fight should happen in front of the camera: raiders falling soon after the portal means they are too weak
    // (the next are tougher), falling at the walls or reaching them means too strong. The line settles part-way down.
    const len = Math.max(1, sim.road.roadCells.length - 1);
    for (const ev of sim.events.all()) {
      if (ev.a <= this.openingIds) continue;
      if (ev.kind === SimEventKind.EnemyLeaked) { this.hpMult = Math.max(0.1, this.hpMult * 0.7); continue; }
      if (ev.kind !== SimEventKind.EnemyKilled) continue;
      const e = sim.enemySlots.find((x) => x.id === ev.a);
      if (!e) continue;
      const along = e.distance.toNumber() / len;            // 0 at the portal, 1 at the gate
      this.hpMult = Math.max(0.1, Math.min(40, this.hpMult * (along < 0.45 ? 1.18 : along > 0.8 ? 0.9 : 1)));
    }
    sim.baseHp = sim.maxBaseHp;                               // the walls never show damage on the title
    this.scene.onTick();
    this.scene.effects.onTick();
  }

  /**
   * The drift: from the opening shot (t = 0, the still's pose exactly) the eye swings slowly round the look point from
   * side to side, with a little rise and fall and a slow push; the device tilt leans it a touch further.
   */
  private placeCamera(t: number): void {
    const w = (2 * Math.PI) / TitleDiorama.DriftSeconds;
    const at = SHOWCASE.at, e0 = SHOWCASE.eye;
    // the drift eases in over its first seconds, so the cross-fade from the still sees the still's own pose
    const ease = Math.min(1, t / TitleDiorama.DriftEaseIn) ** 2;
    const angle = ease * (0.34 * Math.sin(t * w) + this.lean.x * 0.07);
    const push = 1 - ease * 0.07 * Math.sin(t * w * 0.5);
    const rise = ease * (0.45 * Math.sin(t * w * 0.7) - this.lean.y * 0.35);
    const dx = e0.x - at.x, dz = e0.z - at.z, c = Math.cos(angle), s = Math.sin(angle);
    const eye = { x: at.x + (dx * c - dz * s) * push, y: e0.y + rise, z: at.z + (dx * s + dz * c) * push };
    // the key light rides the view's yaw: held at the opening shot's so the shadow map is not redrawn each frame
    const yaw = (Math.atan2(at.x - e0.x, at.z - e0.z) * 180) / Math.PI;
    this.rig.iso.focus(at.x, at.z);                         // the fog band is measured from the look point
    this.rig.iso.setShot({ eye, at, fov: coverFov(innerWidth / Math.max(1, innerHeight)), yaw });
  }

  private random(): number {
    this.seed = (Math.imul(this.seed ^ (this.seed >>> 15), 0x2c1b3c6d) + 0x6d2b79f5) >>> 0;
    return this.seed / 4294967296;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.scene.dispose();
    this.sky.dispose();
    this.hitStop.reset();
    this.rig.iso.setShot(null);
    this.rig.setLook(this.look);
    [GroundMesh.FogStart, GroundMesh.FogFull] = this.prevFog;
    HqMaterials.hazeRange.value.set(GroundMesh.FogStart, GroundMesh.FogFull);
    this.rig.setShadowUpdates("auto");
    this.rig.animatedCasters = 0;
    (WornSkins as unknown as { worn: unknown }).worn = this.prevWorn;
    EnemyWash.on = true;
    audio.holdSfx(false);
  }
}

/** One sim tick, in seconds. */
const TICK = 1 / SimConstants.TicksPerSecond;

/**
 * The live lens for a screen of `aspect` (width / height), so the 3D frames exactly what the still shows there: the
 * still is cover-cropped (CSS object-fit: cover, centred). A screen narrower than the still sees its full height (the
 * sides cropped): the still's own field of view. A wider one sees its full width and a band of its height.
 */
export function coverFov(aspect: number, still = SHOWCASE.aspect, fov = SHOWCASE.fov): number {
  if (aspect <= still) return fov;
  const half = Math.atan(Math.tan(THREE.MathUtils.degToRad(fov) / 2) * (still / aspect));
  return THREE.MathUtils.radToDeg(2 * half);
}

/**
 * Writes the showcase road straight into the sim's road map, as the key-art capture does: the card draft would take
 * minutes of play to lay it. Gorge cells become bridge tiles.
 */
function layRoad(sim: LevelSim, cells: readonly Cell[]): void {
  // RoadMap keeps these private: the sim has no public way to lay a road without its draft rules, and adding one to
  // the domain for a title screen would put presentation in the rules package.
  const R = sim.road as unknown as { occupancy: Uint8Array; designed: Cell[]; designedKinds: TileKind[]; segmentShapes: number[]; version: number; recomputeRoad(): void };
  for (const c of cells) {
    R.occupancy[sim.grid.index(c)] = 1;
    R.designed.push(c);
    R.designedKinds.push(sim.grid.kindAt(c) === CellKind.Chasm ? TileKind.Bridge : TileKind.Normal);
  }
  R.segmentShapes.push(0);
  R.version++;
  R.recomputeRoad();
}
